import {
  BodyText,
  Card,
  Code,
  Heading,
  LinkText,
  TabPanel,
} from '@postscript/components';
import {
  StyledTabPanelIntro,
  StyledTabPanelText,
  TwoColumnLayout,
} from '../../Layout/SimpleStyledComponents';
import TypeWidget from '../../Layout/TypeWidget';
import { FoundationsProps } from '../Foundations';

const FoundationsTypography = ({ selected }: FoundationsProps): JSX.Element => {
  return (
    <TabPanel id="foundations-typography" selected={selected}>
      <StyledTabPanelIntro>
        <BodyText size="medium">
          For most purposes, Postscript uses{' '}
          <LinkText
            href="https://fonts.google.com/specimen/Inter"
            target="_blank"
            rel="noreferrer"
          >
            Inter
          </LinkText>{' '}
          Regular 400 and SemiBold 600.
        </BodyText>
      </StyledTabPanelIntro>

      <TwoColumnLayout margin="0 0 var(--spacing-4)">
        <Card size="large">
          <TypeWidget />
        </Card>
        <div>
          <StyledTabPanelText>
            <Heading size="xx-small">
              <Code>&lt;heading&gt;</Code> elements
            </Heading>
            <BodyText size="small">
              Semantic heading elements don&apos;t necessarily correspond to the
              size of a heading. In other words{' '}
              <Code>size=&quot;x-large&quot;</Code> headings don&apos;t need to
              be <Code>&lt;h1&gt;</Code> elements, but should rather correspond
              to the contextual usage of the element. Use semantic heading
              numbers contextually.
            </BodyText>
          </StyledTabPanelText>
          <StyledTabPanelText>
            <Heading size="xx-small">Links and emphasis</Heading>
            <BodyText size="small">
              Afford links using Inter SemiBold/Roc Grotesk Bold and/or an
              accent color. Links can also be underlined for extra affordance in
              situations where they otherwise may not stand out. Link hover
              colors are a step darker over a dark background, a step lighter
              over a light background.
            </BodyText>
            <BodyText size="small">
              Emphasis is indicated via the 600 weight and an accent color
              (where appropritate).
            </BodyText>
          </StyledTabPanelText>
          <StyledTabPanelText>
            <Heading size="xx-small">Responsiveness</Heading>
            <BodyText size="small">
              Headings and large Body Text can optionally be set to respond down
              to smaller sizes on small screens.
            </BodyText>
          </StyledTabPanelText>
          <StyledTabPanelText>
            <Heading size="xx-small">Line height</Heading>
            <BodyText size="small">
              Line heights are set per font style, chosen for legibility and to
              line up with our 6px baseline. It&apos;s best to use the line
              height associated with the font size you select. Unless you have a
              very good reason, do not override the line height/font size
              pairings.
            </BodyText>
          </StyledTabPanelText>
          <StyledTabPanelText>
            <Heading size="xx-small">Spacing</Heading>
            <BodyText size="small">
              Sizes for type are referred to in Extra Small, Small, Medium,
              Large, and Extra Large intervals. In application, they&apos;re set
              in rem values for flexibility. This allows us to effectively
              increase or decrease the base font size proportionally, based on
              future brand expression. Vertical margins between headlines and
              body text can vary based on text size and the density you want to
              create in your content.
            </BodyText>
          </StyledTabPanelText>
          <StyledTabPanelText>
            <Heading size="xx-small">Hierarchy and pairing</Heading>
            <BodyText size="small">
              Type sizes increase by a perfect fourth increment (1.333),
              adjusted slightly to avoid subpixel values. (Okay, so it&apos;s
              not a perfect fourth.) Working from our 16px base and
              increasing/decreasing by our &quot;perfect&quot; fourth ratio,
              we&apos;ve included 6 heading sizes, 4 body text sizes, and two
              variations eyebrow text.
            </BodyText>

            <Heading size="small">A small heading</Heading>
            <BodyText size="large">
              ...pairs well with large body text.
            </BodyText>

            <Heading size="x-small">An extra small heading</Heading>
            <BodyText size="medium">
              ...pairs well with medium body text.
            </BodyText>

            <Heading size="xx-small">An extra (extra) small heading</Heading>
            <BodyText size="small">
              ...pairs well with small body text.
            </BodyText>
          </StyledTabPanelText>
        </div>
      </TwoColumnLayout>
    </TabPanel>
  );
};

export default FoundationsTypography;
